import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const checkSession = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      console.log('[AuthContext] User authenticated:', response.data.email);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const processSessionId = async (sessionId) => {
    setLoading(true);
    try {
      console.log('[AuthContext] Processing session_id...');
      const response = await axios.post(
        `${backendUrl}/api/auth/session`,
        {},
        {
          headers: {
            'X-Session-ID': sessionId
          },
          withCredentials: true
        }
      );
      
      console.log('[AuthContext] Session created, user:', response.data.email);
      setUser(response.data);
      
      // Clean URL fragment
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      
      // Force recheck session to ensure state is updated
      setTimeout(() => checkSession(), 100);
    } catch (error) {
      console.error('[AuthContext] Session processing error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  // Check for session_id in URL and listen for hash changes
  useEffect(() => {
    const processAuth = async () => {
      // Check for OAuth errors first
      const hash = window.location.hash;
      const params = new URLSearchParams(window.location.search);
      
      // Check for error in hash or query
      if (hash && hash.includes('error=')) {
        const errorMatch = hash.match(/error=([^&]+)/);
        const errorDescMatch = hash.match(/error_description=([^&]+)/);
        if (errorMatch) {
          const error = decodeURIComponent(errorMatch[1]);
          const errorDesc = errorDescMatch ? decodeURIComponent(errorDescMatch[1]) : '';
          console.error('[AuthContext] OAuth error:', error, errorDesc);
          
          // Clean URL
          window.history.replaceState(null, '', window.location.pathname);
          
          // Show user-friendly error
          alert(`Ошибка авторизации: ${errorDesc || error}`);
          setLoading(false);
          return;
        }
      }
      
      // Check URL hash for session_id
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        console.log('[AuthContext] Found session_id in hash');
        await processSessionId(sessionId);
        return;
      }
      
      // Also check query params as fallback
      const sessionId = params.get('session_id');
      if (sessionId) {
        console.log('[AuthContext] Found session_id in query');
        await processSessionId(sessionId);
      }
    };
    
    processAuth();
    
    // Listen for hash changes
    const handleHashChange = () => {
      console.log('[AuthContext] Hash changed, checking for session_id');
      processAuth();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const login = () => {
    try {
      // Method 1: Try full page redirect with origin only
      const origin = window.location.origin;
      const redirectUrl = encodeURIComponent(origin);
      
      console.log('[AuthContext] Initiating login');
      console.log('[AuthContext] Origin:', origin);
      console.log('[AuthContext] Redirect URL:', redirectUrl);
      
      // Clear any existing session_id from URL first
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      
      // Add small delay to ensure clean state
      setTimeout(() => {
        window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
      }, 100);
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${backendUrl}/api/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
};
